import { useState, useEffect, useRef, useMemo } from "react";
import BotonInstructivo from "./BotonInstructivo";
import { subirDatos } from "./syncSupabase";
import { logAction } from "./ALQOSLogger";
import CalculadoraAQL from "./CalculadoraAQL";

// ─────────────────────────────────────────────────────────────────────────
// Control de Calidad de Piso — módulo NUEVO e independiente de Máquinas.js.
// Lo carga el equipo de Calidad (no el maquinista, que ya tiene su propia
// pantalla en Máquinas.js) — es la versión digital de las planillas de
// papel de control por máquina, con distintos chequeos según el tipo de
// proceso de cada máquina.
//
// Este módulo LEE de otros dos (nunca escribe ahí):
//   - alqos_maquinas   → catálogo real de máquinas (Maquinas.js)
//   - alqos_ots        → OTs reales (mismo criterio de búsqueda que ya usa
//                         Maquinas.js: match por ot.op || ot.numero)
// Y ESCRIBE en dos tablas propias:
//   - alqos_control_piso  → cada control cargado (nueva, ver SQL abajo)
//   - alqos_ncs            → cuando un chequeo falla y se decide generar NC,
//                             se agrega un registro más ahí, con el mismo
//                             shape que usa NoConformidades.js — no hace
//                             falta tocar ese archivo porque todo es jsonb.
// ─────────────────────────────────────────────────────────────────────────

const STORAGE_CONTROL = "alqos_control_piso";
const STORAGE_CHECKLIST = "alqos_checklist_tipos";
const STORAGE_MAQUINAS = "alqos_maquinas";
const STORAGE_NC = "alqos_ncs";
const STORAGE_AQL = "alqos_aql_resultados";

const TURNOS = [
  { val: "manana", label: "☀️ Mañana" },
  { val: "tarde", label: "🌤️ Tarde" },
  { val: "noche", label: "🌙 Noche" },
];

const ESTADOS_ITEM = {
  sin_marcar: { label: "Sin marcar", color: "#9aabd4" },
  cumple: { label: "✅ Cumple", color: "#22c55e" },
  no_cumple: { label: "❌ No cumple", color: "#e63946" },
  na: { label: "N/A", color: "#9aabd4" },
};

// Checklist base por tipo de proceso — cubre los ~13 tipos reales del
// catálogo de Máquinas.js (CATALOGO_INICIAL). Se edita desde acá mismo
// (vista "checklist"), esto es solo la semilla inicial: el botón "Cargar
// checklist inicial" solo agrega los tipos que todavía no tengan uno
// cargado, igual criterio que "Cargar catálogo inicial" en Máquinas.js.
const CHECKLIST_INICIAL = [
  { tipoProceso: "Troquelado", items: [
    { texto: "Registro / escuadre correcto", critico: true },
    { texto: "Filo de troquel en buen estado (sin rebabas)", critico: true },
    { texto: "Calibre de cartón según especificación", critico: false },
    { texto: "Contra de troquel bien calzada", critico: false },
    { texto: "Primeras piezas revisadas contra muestra aprobada", critico: true },
  ]},
  { tipoProceso: "Descartonado", items: [
    { texto: "Piezas se sueltan limpias, sin fibra colgando", critico: true },
    { texto: "Sin piezas rotas o dañadas en el descartonado", critico: false },
  ]},
  { tipoProceso: "Cajonera", items: [
    { texto: "Escuadre de la caja correcto", critico: true },
    { texto: "Pliegues sin fisuras ni quiebres", critico: false },
  ]},
  { tipoProceso: "Acanalado", items: [
    { texto: "Profundidad de canal según especificación", critico: true },
    { texto: "Canal centrado, sin desvío", critico: false },
  ]},
  { tipoProceso: "Gofrado", items: [
    { texto: "Relieve parejo, sin zonas sin marcar", critico: true },
    { texto: "Registro del gofrado contra el diseño", critico: true },
  ]},
  { tipoProceso: "Montaje", items: [
    { texto: "Pegado sin burbujas ni despegues", critico: true },
    { texto: "Escuadre del montaje correcto", critico: false },
  ]},
  { tipoProceso: "Corte", items: [
    { texto: "Medida de corte según especificación", critico: true },
    { texto: "Corte limpio, sin rebabas ni fibra suelta", critico: false },
    { texto: "Escuadre del corte correcto", critico: true },
  ]},
  { tipoProceso: "Laminado", items: [
    { texto: "Laminado sin burbujas ni despegues", critico: true },
    { texto: "Brillo/mate uniforme en toda la superficie", critico: false },
  ]},
  { tipoProceso: "Entapado", items: [
    { texto: "Tapa cierra correctamente, sin forzar", critico: true },
    { texto: "Pegado de tapa firme, sin despegues", critico: false },
  ]},
  { tipoProceso: "Aplicación de imanes", items: [
    { texto: "Imán centrado según especificación", critico: false },
    { texto: "Imán pegado firme, no se despega al abrir/cerrar", critico: true },
  ]},
  { tipoProceso: "Serigrafía", items: [
    { texto: "Registro de color correcto", critico: true },
    { texto: "Densidad de tinta uniforme", critico: false },
    { texto: "Sin manchas ni chorreado de tinta", critico: true },
  ]},
  { tipoProceso: "Armado", items: [
    { texto: "Piezas armadas según secuencia correcta", critico: true },
    { texto: "Sin piezas faltantes en el armado", critico: true },
  ]},
  { tipoProceso: "Pegado", items: [
    { texto: "Pegado firme, sin despegues", critico: true },
    { texto: "Sin exceso de pegamento visible", critico: false },
  ]},
];

const storage = {
  get: (k, def = []) => { try { return JSON.parse(localStorage.getItem(k) || JSON.stringify(def)); } catch { return def; } },
  set: (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch (e) { console.error(e); } },
};

// Mismo bug que se corrigió en generarNCI() de NoConformidades.js: acá se
// evita desde el arranque buscando el número más alto ya usado, no
// lista.length+1 — así no se pisan códigos si algún registro se borró.
function generarCodigoControl(lista) {
  const anio = new Date().getFullYear();
  const prefijo = `CTRL-${anio}-`;
  const max = lista.reduce((m, c) => {
    if (!c.codigo?.startsWith(prefijo)) return m;
    const n = parseInt(c.codigo.slice(prefijo.length), 10);
    return isNaN(n) ? m : Math.max(m, n);
  }, 0);
  return `${prefijo}${String(max + 1).padStart(3, "0")}`;
}

// Mismo criterio para el NCI que se genera acá — busca el número más alto
// ya usado en alqos_ncs para el año actual, sin asumir que la lista está
// completa ni en orden.
function generarNCIDesdeControl(listaNC) {
  const anio = new Date().getFullYear();
  const prefijo = `NCI-${anio}-`;
  const max = listaNC.reduce((m, n) => {
    if (!n.nci?.startsWith(prefijo)) return m;
    const num = parseInt(n.nci.slice(prefijo.length), 10);
    return isNaN(num) ? m : Math.max(m, num);
  }, 0);
  return `${prefijo}${String(max + 1).padStart(3, "0")}`;
}

// Estado en blanco para un checklist recién cargado — un item por entrada
// del catálogo del tipo de proceso de la máquina elegida.
function itemsEnBlanco(catalogoItems) {
  return (catalogoItems || []).map((it, i) => ({
    idx: i,
    texto: it.texto,
    critico: !!it.critico,
    estado: "sin_marcar",
    descripcion: "",
    generarNC: !!it.critico,
  }));
}

// ─── Firma táctil — canvas simple con puntero/mouse/touch, sin librería.
function FirmaCanvas({ oscuro, borde, onCambio }) {
  const canvasRef = useRef(null);
  const dibujando = useRef(false);
  const vacio = useRef(true);

  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    const ctx = cv.getContext("2d");
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.strokeStyle = oscuro ? "#EDF2FA" : "#09184D";
  }, [oscuro]);

  const posDesdeEvento = (e) => {
    const cv = canvasRef.current;
    const rect = cv.getBoundingClientRect();
    const p = e.touches ? e.touches[0] : e;
    return { x: p.clientX - rect.left, y: p.clientY - rect.top };
  };

  const empezar = (e) => {
    e.preventDefault();
    dibujando.current = true;
    const { x, y } = posDesdeEvento(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const mover = (e) => {
    if (!dibujando.current) return;
    e.preventDefault();
    const { x, y } = posDesdeEvento(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(x, y);
    ctx.stroke();
    vacio.current = false;
  };
  const terminar = () => {
    if (!dibujando.current) return;
    dibujando.current = false;
    onCambio(vacio.current ? null : canvasRef.current.toDataURL("image/png"));
  };
  const limpiar = () => {
    const cv = canvasRef.current;
    cv.getContext("2d").clearRect(0, 0, cv.width, cv.height);
    vacio.current = true;
    onCambio(null);
  };

  return (
    <div>
      <canvas
        ref={canvasRef}
        width={340}
        height={120}
        style={{ width: "100%", maxWidth: "340px", height: "120px", borderRadius: "8px", border: `1px dashed ${borde}`, background: oscuro ? "#091840" : "#F9FAFB", touchAction: "none", cursor: "crosshair" }}
        onMouseDown={empezar} onMouseMove={mover} onMouseUp={terminar} onMouseLeave={terminar}
        onTouchStart={empezar} onTouchMove={mover} onTouchEnd={terminar}
      />
      <button type="button" onClick={limpiar} style={{ marginTop: "6px", padding: "4px 10px", background: "none", border: `1px solid ${borde}`, borderRadius: "6px", cursor: "pointer", fontSize: "11px", color: "#e63946" }}>Borrar firma</button>
    </div>
  );
}

export default function ControlCalidadPiso({ onVolver, oscuro, usuario }) {
  const [controles, setControles] = useState(() => storage.get(STORAGE_CONTROL));
  const [checklistTipos, setChecklistTipos] = useState(() => storage.get(STORAGE_CHECKLIST));
  const [maquinas] = useState(() => storage.get(STORAGE_MAQUINAS));
  const [aqlResultados, setAqlResultados] = useState(() => storage.get(STORAGE_AQL));
  const [ots] = useState(() => { try { return JSON.parse(localStorage.getItem("alqos_ots") || "[]"); } catch { return []; } });

  const [vista, setVista] = useState("listado"); // listado | nueva | checklist
  const [filtroMaquina, setFiltroMaquina] = useState("todas");

  // ── Formulario de carga nueva ──
  const [maquinaId, setMaquinaId] = useState("");
  const [turno, setTurno] = useState("manana");
  const [numeroOtBuscada, setNumeroOtBuscada] = useState("");
  const [otEncontrada, setOtEncontrada] = useState(null);
  const [errorBusquedaOt, setErrorBusquedaOt] = useState("");
  const [itemsForm, setItemsForm] = useState([]);
  const [firmante, setFirmante] = useState("");
  const [firmaImagen, setFirmaImagen] = useState(null);
  const [errorGuardar, setErrorGuardar] = useState("");
  const [confirmacion, setConfirmacion] = useState(null); // { codigo, ncisGenerados: [{nci,id}] }
  const [retrabajos, setRetrabajos] = useState({}); // { [ncId]: {realizado, cantidad, horas, nota} }

  useEffect(() => {
    async function cargar() {
      try {
        const { supabase } = await import("./supabase");
        const { data } = await supabase.from("control_piso").select("data");
        if (data?.length) {
          const remotos = data.map(r => r.data);
          setControles(remotos);
          storage.set(STORAGE_CONTROL, remotos);
        }
      } catch { /* se queda con lo local */ }
    }
    cargar();
  }, []);

  const maquina = useMemo(() => maquinas.find(m => String(m.id) === String(maquinaId)) || null, [maquinas, maquinaId]);

  const bg = oscuro ? "#09184D" : "#F0EEFF";
  const cardBg = oscuro ? "#0d2160" : "white";
  const texto = oscuro ? "#EDF2FA" : "#2F3E2E";
  const borde = oscuro ? "#1e3a8a" : "#eee";
  const navBg = oscuro ? "#060f30" : "#09184D";
  const subtexto = oscuro ? "#9aabd4" : "#808085";
  const esAdmin = usuario?.rol === "Administrador";
  const puedeAdministrarChecklist = esAdmin || usuario?.rol === "Calidad";

  const inp = { width: "100%", padding: "8px 12px", borderRadius: "8px", border: `1px solid ${borde}`, fontSize: "13px", boxSizing: "border-box", marginTop: "4px", background: cardBg, color: texto, fontFamily: "sans-serif" };
  const lbl = { fontSize: "12px", color: oscuro ? "#9aabd4" : "#555", fontWeight: "600", display: "block", marginTop: "12px" };
  const card = { background: cardBg, borderRadius: "12px", padding: "1.5rem", border: `1px solid ${borde}`, marginBottom: "12px" };

  const Header = ({ titulo, onBack, extra }) => (
    <div style={{ background: navBg, padding: "1rem 1.5rem", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 100, marginBottom: "1.5rem" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <button onClick={onBack} style={{ background: "rgba(255,255,255,0.1)", border: "none", color: "white", padding: "6px 14px", borderRadius: "10px", cursor: "pointer", fontSize: "13px" }}>← Volver</button>
        <span style={{ color: "white", fontWeight: "700", fontSize: "16px" }}>{titulo}</span>
      </div>
      {extra}
    </div>
  );

  // ── Selección de máquina → carga el checklist de su tipo de proceso ──
  const elegirMaquina = (id) => {
    setMaquinaId(id);
    const m = maquinas.find(mm => String(mm.id) === String(id));
    const catalogo = checklistTipos.find(c => c.tipoProceso === m?.tipoProceso);
    setItemsForm(itemsEnBlanco(catalogo?.items));
  };

  const buscarOT = () => {
    const buscado = numeroOtBuscada.trim().toLowerCase();
    if (!buscado) { setErrorBusquedaOt("Ingresá un número de OT."); setOtEncontrada(null); return; }
    const encontrada = ots.find(ot => {
      const numero = (ot.op || ot.numero || "").toString().trim().toLowerCase();
      return numero === buscado;
    });
    if (!encontrada) {
      setOtEncontrada(null);
      setErrorBusquedaOt(`⛔ OT "${numeroOtBuscada}" no encontrada en ALQOS.`);
      return;
    }
    setErrorBusquedaOt("");
    setOtEncontrada(encontrada);
  };

  const marcarItem = (idx, estado) => {
    setItemsForm(items => items.map(it => it.idx === idx ? { ...it, estado, generarNC: estado === "no_cumple" ? it.generarNC : false } : it));
  };
  const editarItem = (idx, campos) => {
    setItemsForm(items => items.map(it => it.idx === idx ? { ...it, ...campos } : it));
  };

  const resetForm = () => {
    setMaquinaId(""); setTurno("manana");
    setNumeroOtBuscada(""); setOtEncontrada(null); setErrorBusquedaOt("");
    setItemsForm([]); setFirmante(""); setFirmaImagen(null); setErrorGuardar("");
  };

  // Arma el registro de NC con el mismo shape que usa guardarNC() en
  // NoConformidades.js, más los campos de trazabilidad de origen — es
  // jsonb, así que agregarlos acá no rompe nada del lado de ese módulo.
  const construirNC = (item, controlCodigo) => {
    const ahora = new Date();
    return {
      id: Date.now() + Math.floor(Math.random() * 1000),
      nci: generarNCIDesdeControl(storage.get(STORAGE_NC)),
      fecha: ahora.toISOString().slice(0, 16),
      proceso: maquina?.tipoProceso || "",
      sector: maquina?.ubicacion || "",
      trabajo: otEncontrada?.trabajo || "",
      codigoProducto: "",
      cliente: otEncontrada?.cliente || "",
      op: otEncontrada?.op || otEncontrada?.numero || "",
      origen: "Control de Piso",
      deteccion: "CONTROL DE CALIDAD",
      tipoNC: "INTERNA",
      severidad: item.critico ? "mayor" : "menor",
      escalamiento: "bajo",
      descripcion: `[${maquina?.nombre || "Máquina"}] ${item.texto}: ${item.descripcion || "sin detalle"}`,
      estado: "abierta",
      creadoPor: usuario?.nombre || "Sistema",
      fechaCreacion: ahora.toISOString(),
      maquina: maquina?.nombre || "",
      maquinaId: maquina?.id || null,
      controlPisoCodigo: controlCodigo,
      retrabajoRealizado: false,
      _migracionHistorica: false,
      _fuenteOriginal: "Control de Calidad de Piso",
    };
  };

  const guardar = () => {
    setErrorGuardar("");
    if (!maquinaId) { setErrorGuardar("Elegí una máquina."); return; }
    if (itemsForm.some(it => it.estado === "sin_marcar")) { setErrorGuardar("Todos los ítems tienen que quedar marcados (Cumple / No cumple / N/A)."); return; }
    const fallidosSinDescripcion = itemsForm.some(it => it.estado === "no_cumple" && !it.descripcion.trim());
    if (fallidosSinDescripcion) { setErrorGuardar("Los ítems marcados 'No cumple' necesitan una descripción breve."); return; }
    if (!firmante.trim()) { setErrorGuardar("Falta el nombre de quien firma el control."); return; }
    if (!firmaImagen) { setErrorGuardar("Falta la firma."); return; }

    const codigo = generarCodigoControl(controles);
    const registro = {
      id: Date.now(),
      codigo,
      maquinaId: maquina.id,
      maquinaNombre: maquina.nombre + (maquina.modelo ? ` — ${maquina.modelo}` : ""),
      tipoProceso: maquina.tipoProceso || "",
      turno,
      otNumero: otEncontrada?.op || otEncontrada?.numero || "",
      otCliente: otEncontrada?.cliente || "",
      otTrabajo: otEncontrada?.trabajo || "",
      items: itemsForm,
      firmante: firmante.trim(),
      firmaImagen,
      registradoPor: usuario?.nombre || "Sistema",
      fechaCreacion: new Date().toISOString(),
    };

    const nuevosControles = [...controles, registro];
    setControles(nuevosControles);
    storage.set(STORAGE_CONTROL, nuevosControles);
    subirDatos(STORAGE_CONTROL);
    logAction("ControlCalidadPiso", "crear", { entidad: "ControlPiso", idEntidad: codigo, despues: registro }, usuario);

    // Genera una NC por cada ítem fallido con "Generar NC" tildado.
    const ncsActuales = storage.get(STORAGE_NC);
    const nuevasNC = itemsForm
      .filter(it => it.estado === "no_cumple" && it.generarNC)
      .map(it => construirNC(it, codigo));
    if (nuevasNC.length > 0) {
      const todasNC = [...ncsActuales, ...nuevasNC];
      storage.set(STORAGE_NC, todasNC);
      subirDatos(STORAGE_NC);
      nuevasNC.forEach(nc => logAction("NC", "crear", { entidad: "NC", idEntidad: nc.nci, despues: nc, detalle: "Generada desde Control de Calidad de Piso" }, usuario));
    }

    setConfirmacion({ codigo, ncisGenerados: nuevasNC.map(nc => ({ nci: nc.nci, id: nc.id })) });
  };

  // Marca retrabajo en una NC ya generada, sin volver a abrir NoConformidades.js.
  const guardarRetrabajo = (ncId) => {
    const r = retrabajos[ncId] || {};
    const ncsActuales = storage.get(STORAGE_NC);
    const actualizadas = ncsActuales.map(nc => nc.id === ncId ? {
      ...nc,
      retrabajoRealizado: !!r.realizado,
      retrabajoCantidad: r.cantidad || "",
      retrabajoHoras: r.horas || "",
      retrabajoNota: r.nota || "",
    } : nc);
    storage.set(STORAGE_NC, actualizadas);
    subirDatos(STORAGE_NC);
  };

  const cerrarConfirmacion = () => {
    setConfirmacion(null);
    setRetrabajos({});
    resetForm();
    setVista("listado");
  };

  const cargarChecklistInicial = () => {
    const faltantes = CHECKLIST_INICIAL.filter(c => !checklistTipos.some(ct => ct.tipoProceso === c.tipoProceso));
    if (faltantes.length === 0) { alert("Ya están cargados los checklists de todos los tipos de proceso base."); return; }
    const nuevos = [...checklistTipos, ...faltantes];
    setChecklistTipos(nuevos);
    storage.set(STORAGE_CHECKLIST, nuevos);
    subirDatos(STORAGE_CHECKLIST);
    alert(`✅ Se agregaron ${faltantes.length} checklist(s) de tipo de proceso.`);
  };

  const agregarItemChecklist = (tipoProceso) => {
    const texto = prompt("Texto del nuevo ítem de chequeo:");
    if (!texto?.trim()) return;
    const critico = window.confirm("¿Es un ítem crítico? (pre-marca 'Generar NC' automáticamente al fallar)");
    const nuevos = checklistTipos.map(c => c.tipoProceso === tipoProceso ? { ...c, items: [...c.items, { texto: texto.trim(), critico }] } : c);
    setChecklistTipos(nuevos);
    storage.set(STORAGE_CHECKLIST, nuevos);
    subirDatos(STORAGE_CHECKLIST);
  };
  const quitarItemChecklist = (tipoProceso, idx) => {
    const nuevos = checklistTipos.map(c => c.tipoProceso === tipoProceso ? { ...c, items: c.items.filter((_, i) => i !== idx) } : c);
    setChecklistTipos(nuevos);
    storage.set(STORAGE_CHECKLIST, nuevos);
    subirDatos(STORAGE_CHECKLIST);
  };

  // Guarda un resultado de la calculadora AQL como registro propio, con
  // trazabilidad (Fase G) automática igual que el resto de las tablas
  // nuevas. Queda disponible acá para conectarlo después con la
  // generación del Certificado de Calidad (COA) — ver INSTALAR_ControlCalidadPiso.md,
  // sección AQL→COA, para el patch exacto sobre el generador de PDF real.
  const guardarResultadoAQL = (resultado) => {
    const registro = { id: Date.now(), ...resultado, registradoPor: usuario?.nombre || "Sistema" };
    const nuevos = [...aqlResultados, registro];
    setAqlResultados(nuevos);
    storage.set(STORAGE_AQL, nuevos);
    subirDatos(STORAGE_AQL);
    logAction("ControlCalidadPiso", "crear", { entidad: "ResultadoAQL", idEntidad: registro.id, despues: registro }, usuario);
    alert(`✅ Resultado guardado: lote ${resultado.decision === "aceptado" ? "ACEPTADO" : resultado.decision === "rechazado" ? "RECHAZADO" : "calculado"} (letra ${resultado.letra}, n=${resultado.n}, Ac=${resultado.Ac}, Re=${resultado.Re}).`);
    setVista("listado");
  };

  // ─── Vista: Calculadora AQL (IRAM 15 / ISO 2859-1) ───
  if (vista === "aql") return (
    <CalculadoraAQL oscuro={oscuro} onVolver={() => setVista("listado")} onGuardarResultado={guardarResultadoAQL} />
  );

  // ─── Vista: Administrar checklist por tipo de proceso ───
  if (vista === "checklist") return (
    <div style={{ minHeight: "100vh", background: bg, fontFamily: "sans-serif" }}>
      <Header titulo="Checklist por tipo de proceso" onBack={() => setVista("listado")}
        extra={<button onClick={cargarChecklistInicial} style={{ padding: "6px 14px", background: "rgba(255,255,255,0.15)", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "13px", fontWeight: "600" }}>📋 Cargar checklist inicial</button>} />
      <div style={{ padding: "0 2rem 2rem", maxWidth: "700px", margin: "0 auto" }}>
        {checklistTipos.length === 0 && <p style={{ color: subtexto, fontSize: "13px" }}>Todavía no hay ningún checklist cargado — usá "Cargar checklist inicial" para partir de una base y editarla.</p>}
        {checklistTipos.map(c => (
          <div key={c.tipoProceso} style={card}>
            <h3 style={{ fontSize: "14px", fontWeight: "700", color: "#1C3FAA", margin: "0 0 10px" }}>{c.tipoProceso}</h3>
            {c.items.map((it, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: "8px", padding: "6px 0", borderBottom: `0.5px solid ${borde}` }}>
                <span style={{ fontSize: "12px", color: texto, flex: 1 }}>{it.texto}{it.critico ? " ⚠️" : ""}</span>
                <button onClick={() => quitarItemChecklist(c.tipoProceso, i)} style={{ background: "none", border: "none", color: "#e63946", cursor: "pointer", fontSize: "12px" }}>Quitar</button>
              </div>
            ))}
            <button onClick={() => agregarItemChecklist(c.tipoProceso)} style={{ marginTop: "10px", padding: "6px 12px", background: "none", border: `1px solid ${borde}`, borderRadius: "8px", cursor: "pointer", fontSize: "12px", color: "#1C3FAA" }}>+ Agregar ítem</button>
          </div>
        ))}
      </div>
    </div>
  );

  // ─── Vista: Confirmación post-guardado (NC generadas + retrabajo) ───
  if (confirmacion) return (
    <div style={{ minHeight: "100vh", background: bg, fontFamily: "sans-serif", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: cardBg, borderRadius: "16px", padding: "2rem", maxWidth: "440px", width: "90%", border: `1px solid ${borde}` }}>
        <p style={{ fontSize: "32px", textAlign: "center", marginBottom: "0.5rem" }}>✅</p>
        <p style={{ fontSize: "15px", fontWeight: "700", color: texto, textAlign: "center", marginBottom: "1rem" }}>Control {confirmacion.codigo} guardado</p>
        {confirmacion.ncisGenerados.length === 0 ? (
          <p style={{ fontSize: "13px", color: subtexto, textAlign: "center" }}>No se generó ninguna NC.</p>
        ) : (
          <div>
            <p style={{ fontSize: "13px", color: texto, marginBottom: "10px" }}>Se generaron {confirmacion.ncisGenerados.length} NC:</p>
            {confirmacion.ncisGenerados.map(nc => (
              <div key={nc.id} style={{ background: oscuro ? "#091840" : "#f8f9fa", borderRadius: "10px", padding: "10px 14px", marginBottom: "10px", border: `1px solid ${borde}` }}>
                <p style={{ fontSize: "13px", fontWeight: "700", color: "#e63946", margin: "0 0 8px" }}>{nc.nci}</p>
                <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12px", color: texto }}>
                  <input type="checkbox" checked={!!retrabajos[nc.id]?.realizado}
                    onChange={e => setRetrabajos(r => ({ ...r, [nc.id]: { ...r[nc.id], realizado: e.target.checked } }))} />
                  ¿Se hizo retrabajo?
                </label>
                {retrabajos[nc.id]?.realizado && (
                  <div style={{ marginTop: "8px", display: "flex", gap: "8px" }}>
                    <input placeholder="Cantidad" style={{ ...inp, marginTop: 0, fontSize: "12px" }}
                      onChange={e => setRetrabajos(r => ({ ...r, [nc.id]: { ...r[nc.id], cantidad: e.target.value } }))} />
                    <input placeholder="Horas" style={{ ...inp, marginTop: 0, fontSize: "12px" }}
                      onChange={e => setRetrabajos(r => ({ ...r, [nc.id]: { ...r[nc.id], horas: e.target.value } }))} />
                  </div>
                )}
                {retrabajos[nc.id]?.realizado && (
                  <button onClick={() => guardarRetrabajo(nc.id)} style={{ marginTop: "8px", padding: "5px 10px", background: "#1C3FAA", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "11px", fontWeight: "600" }}>Guardar retrabajo</button>
                )}
              </div>
            ))}
          </div>
        )}
        <button onClick={cerrarConfirmacion} style={{ width: "100%", padding: "12px", background: "#1C3FAA", color: "white", border: "none", borderRadius: "10px", fontSize: "14px", fontWeight: "600", cursor: "pointer", marginTop: "8px" }}>Listo</button>
      </div>
    </div>
  );

  // ─── Vista: Nueva carga ───
  if (vista === "nueva") return (
    <div style={{ minHeight: "100vh", background: bg, fontFamily: "sans-serif" }}>
      <Header titulo="Nuevo control de piso" onBack={() => { resetForm(); setVista("listado"); }} />
      <div style={{ padding: "0 2rem 2rem", maxWidth: "700px", margin: "0 auto" }}>
        <div style={card}>
          <span style={lbl}>Máquina *</span>
          <select value={maquinaId} onChange={e => elegirMaquina(e.target.value)} style={inp}>
            <option value="">Elegí una máquina...</option>
            {maquinas.map(m => <option key={m.id} value={m.id}>{m.nombre}{m.modelo ? ` — ${m.modelo}` : ""} ({m.tipoProceso || "sin tipo"})</option>)}
          </select>

          <span style={lbl}>Turno *</span>
          <div style={{ display: "flex", gap: "8px", marginTop: "6px" }}>
            {TURNOS.map(t => (
              <button key={t.val} onClick={() => setTurno(t.val)}
                style={{ padding: "8px 14px", borderRadius: "10px", fontSize: "12px", cursor: "pointer", fontWeight: "600", border: turno === t.val ? "2px solid #1C3FAA" : `1px solid ${borde}`, background: turno === t.val ? "rgba(28,63,170,.1)" : cardBg, color: turno === t.val ? "#1C3FAA" : texto }}>{t.label}</button>
            ))}
          </div>

          <span style={lbl}>OT (opcional)</span>
          <div style={{ display: "flex", gap: "8px" }}>
            <input value={numeroOtBuscada} onChange={e => setNumeroOtBuscada(e.target.value)} onKeyDown={e => e.key === "Enter" && buscarOT()}
              placeholder="Ej: 9338" style={{ ...inp, marginTop: 0, flex: 1 }} />
            <button onClick={buscarOT} style={{ padding: "8px 16px", background: "#2A7FFF", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "13px", fontWeight: "600", marginTop: 0 }}>Buscar</button>
          </div>
          {errorBusquedaOt && <p style={{ color: "#e63946", fontSize: "11px", marginTop: "6px" }}>{errorBusquedaOt}</p>}
          {otEncontrada && <p style={{ color: "#22c55e", fontSize: "12px", marginTop: "6px", fontWeight: "600" }}>✅ {otEncontrada.op || otEncontrada.numero} — {otEncontrada.cliente || "sin cliente"}</p>}
        </div>

        {maquinaId && (
          <div style={card}>
            <h3 style={{ fontSize: "14px", fontWeight: "700", color: "#1C3FAA", margin: "0 0 12px" }}>Checklist — {maquina?.tipoProceso || "sin tipo de proceso"}</h3>
            {itemsForm.length === 0 && <p style={{ fontSize: "12px", color: subtexto }}>Esta máquina no tiene checklist cargado todavía para su tipo de proceso ("{maquina?.tipoProceso}"). Armalo desde "⚙️ Administrar checklist".</p>}
            {itemsForm.map(it => (
              <div key={it.idx} style={{ padding: "10px 0", borderBottom: `0.5px solid ${borde}` }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "8px" }}>
                  <span style={{ fontSize: "13px", color: texto, fontWeight: "500" }}>{it.texto}{it.critico ? " ⚠️" : ""}</span>
                  <div style={{ display: "flex", gap: "6px" }}>
                    {["cumple", "no_cumple", "na"].map(estado => (
                      <button key={estado} onClick={() => marcarItem(it.idx, estado)}
                        style={{ padding: "6px 10px", borderRadius: "8px", fontSize: "11px", cursor: "pointer", fontWeight: "600", border: it.estado === estado ? `2px solid ${ESTADOS_ITEM[estado].color}` : `1px solid ${borde}`, background: it.estado === estado ? `${ESTADOS_ITEM[estado].color}22` : cardBg, color: it.estado === estado ? ESTADOS_ITEM[estado].color : subtexto }}>{ESTADOS_ITEM[estado].label}</button>
                    ))}
                  </div>
                </div>
                {it.estado === "no_cumple" && (
                  <div style={{ marginTop: "8px", background: oscuro ? "#1a0505" : "#fff5f5", borderRadius: "8px", padding: "10px 12px" }}>
                    <span style={{ ...lbl, marginTop: 0 }}>¿Qué pasó? *</span>
                    <textarea rows={2} value={it.descripcion} onChange={e => editarItem(it.idx, { descripcion: e.target.value })} style={{ ...inp, resize: "none" }} placeholder="Descripción breve del desvío" />
                    <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12px", color: texto, marginTop: "8px" }}>
                      <input type="checkbox" checked={it.generarNC} onChange={e => editarItem(it.idx, { generarNC: e.target.checked })} />
                      Generar NC{it.critico ? " (crítico, pre-marcado)" : ""}
                    </label>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {maquinaId && itemsForm.length > 0 && (
          <div style={card}>
            <h3 style={{ fontSize: "14px", fontWeight: "700", color: "#1C3FAA", margin: "0 0 4px" }}>Firma</h3>
            <span style={lbl}>Nombre de quien controla *</span>
            <input value={firmante} onChange={e => setFirmante(e.target.value)} style={inp} placeholder="Nombre y apellido" />
            <span style={{ ...lbl, marginBottom: "4px" }}>Firma (dibujá con el dedo) *</span>
            <FirmaCanvas oscuro={oscuro} borde={borde} onCambio={setFirmaImagen} />
          </div>
        )}

        {errorGuardar && <p style={{ color: "#e63946", fontSize: "13px", fontWeight: "600" }}>{errorGuardar}</p>}

        {maquinaId && itemsForm.length > 0 && (
          <button onClick={guardar} style={{ width: "100%", padding: "14px", background: "#1C3FAA", color: "white", border: "none", borderRadius: "10px", fontSize: "15px", fontWeight: "600", cursor: "pointer" }}>Guardar control</button>
        )}
      </div>
    </div>
  );

  // ─── Vista: Listado ───
  const controlesFiltrados = controles
    .filter(c => filtroMaquina === "todas" || String(c.maquinaId) === filtroMaquina)
    .sort((a, b) => b.fechaCreacion.localeCompare(a.fechaCreacion));

  return (
    <div style={{ minHeight: "100vh", background: bg, fontFamily: "sans-serif" }}>
      <div style={{ background: navBg, padding: "1rem 1.5rem", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 100 }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <button onClick={onVolver} style={{ background: "rgba(255,255,255,0.1)", border: "none", color: "white", padding: "6px 14px", borderRadius: "10px", cursor: "pointer", fontSize: "13px" }}>← Menú</button>
          <BotonInstructivo moduloKey="control-calidad-piso" oscuro={oscuro} />
          <span style={{ color: "white", fontWeight: "700", fontSize: "16px" }}>Control de Calidad de Piso</span>
        </div>
        <div style={{ display: "flex", gap: "8px" }}>
          {puedeAdministrarChecklist && (
            <button onClick={() => setVista("checklist")} style={{ padding: "6px 14px", background: "rgba(255,255,255,0.15)", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "13px", fontWeight: "600" }}>⚙️ Administrar checklist</button>
          )}
          <button onClick={() => setVista("aql")} style={{ padding: "6px 14px", background: "rgba(255,255,255,0.15)", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "13px", fontWeight: "600" }}>📐 Calculadora AQL</button>
          <button onClick={() => setVista("nueva")} style={{ padding: "6px 14px", background: "#2A7FFF", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "13px", fontWeight: "600" }}>+ Nuevo control</button>
        </div>
      </div>

      <div style={{ padding: "1rem 1.5rem" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: "10px", marginBottom: "1.5rem" }}>
          {[
            ["Controles cargados", controles.length, "#1C3FAA"],
            ["Con desvíos", controles.filter(c => c.items.some(i => i.estado === "no_cumple")).length, "#e63946"],
            ["NC generadas desde acá", controles.reduce((acc, c) => acc + c.items.filter(i => i.estado === "no_cumple" && i.generarNC).length, 0), "#f59e0b"],
          ].map(([lab, val, col]) => (
            <div key={lab} style={{ background: cardBg, borderRadius: "12px", padding: "1rem", border: `1px solid ${borde}`, textAlign: "center" }}>
              <p style={{ fontSize: "22px", fontWeight: "700", color: col, margin: 0 }}>{val}</p>
              <p style={{ fontSize: "11px", color: subtexto, margin: "4px 0 0" }}>{lab}</p>
            </div>
          ))}
        </div>

        <select value={filtroMaquina} onChange={e => setFiltroMaquina(e.target.value)} style={{ ...inp, marginTop: 0, maxWidth: "300px", marginBottom: "12px" }}>
          <option value="todas">Todas las máquinas</option>
          {maquinas.map(m => <option key={m.id} value={m.id}>{m.nombre}{m.modelo ? ` — ${m.modelo}` : ""}</option>)}
        </select>

        {controlesFiltrados.length === 0
          ? <div style={{ textAlign: "center", padding: "4rem" }}>
              <p style={{ fontSize: "40px" }}>📋</p>
              <p style={{ fontSize: "15px", color: texto }}>No hay controles cargados todavía</p>
            </div>
          : controlesFiltrados.map(c => {
              const fallidos = c.items.filter(i => i.estado === "no_cumple").length;
              return (
                <div key={c.id} style={{ background: cardBg, borderRadius: "12px", padding: "1.2rem 1.5rem", border: `1px solid ${borde}`, marginBottom: "10px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "6px" }}>
                    <div>
                      <span style={{ fontWeight: "700", color: "#1C3FAA", fontSize: "13px" }}>{c.codigo}</span>
                      <span style={{ marginLeft: "10px", fontSize: "13px", color: texto, fontWeight: "600" }}>{c.maquinaNombre}</span>
                      <p style={{ fontSize: "12px", color: subtexto, marginTop: "4px" }}>
                        {TURNOS.find(t => t.val === c.turno)?.label} · {c.fechaCreacion?.slice(0, 16).replace("T", " ")} · firmó {c.firmante}
                        {c.otNumero ? ` · OT ${c.otNumero}` : ""}
                      </p>
                    </div>
                    <span style={{ fontSize: "11px", fontWeight: "700", color: fallidos > 0 ? "#e63946" : "#22c55e" }}>
                      {fallidos > 0 ? `⚠️ ${fallidos} desvío(s)` : "✅ Sin desvíos"}
                    </span>
                  </div>
                </div>
              );
            })
        }
      </div>
    </div>
  );
}
